import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiSmile, FiCornerUpLeft } from 'react-icons/fi';
import { supabase } from '../../config/supabase';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const getUserName = (user) => {
    if (!user) return 'Unknown';
    return user.full_name || user.username || user.email || 'Unknown';
};

const MessageReactions = ({ messageId, currentUserId, isOwn, onReply }) => {
    const [reactions, setReactions] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const pickerRef = useRef(null);

    const fetchReactions = async () => {
        if (!messageId) return;

        const { data, error } = await supabase
            .from('message_reactions')
            .select(`
                id,
                message_id,
                user_id,
                emoji,
                created_at,
                user:users (
                    id,
                    username,
                    full_name,
                    email,
                    avatar_url
                )
            `)
            .eq('message_id', messageId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching reactions:', error);
            return;
        }

        setReactions(data || []);
    };

    useEffect(() => {
        fetchReactions();

        const channel = supabase
            .channel(`message-reactions-${messageId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'message_reactions',
                    filter: `message_id=eq.${messageId}`,
                },
                () => {
                    fetchReactions();
                }
            )
            .subscribe();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [messageId]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const groupedReactions = useMemo(() => {
        return reactions.reduce((acc, reaction) => {
            if (!acc[reaction.emoji]) {
                acc[reaction.emoji] = {
                    emoji: reaction.emoji,
                    count: 0,
                    users: [],
                    reactedByMe: false,
                };
            }

            acc[reaction.emoji].count += 1;
            acc[reaction.emoji].users.push(getUserName(reaction.user));

            if (reaction.user_id === currentUserId) {
                acc[reaction.emoji].reactedByMe = true;
            }

            return acc;
        }, {});
    }, [reactions, currentUserId]);

    const myReaction = reactions.find(r => r.user_id === currentUserId);

    const handleReactionClick = async (emoji) => {
        if (!messageId || !currentUserId) return;

        try {
            if (myReaction?.emoji === emoji) {
                const { error } = await supabase
                    .from('message_reactions')
                    .delete()
                    .eq('message_id', messageId)
                    .eq('user_id', currentUserId);

                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('message_reactions')
                    .upsert(
                        {
                            message_id: messageId,
                            user_id: currentUserId,
                            emoji,
                        },
                        {
                            onConflict: 'message_id,user_id',
                        }
                    );

                if (error) throw error;
            }

            setShowPicker(false);
            await fetchReactions();
        } catch (error) {
            console.error('Error saving reaction:', error);
        }
    };

    return (
        <>
            {/* Floating hover button */}
            <div
                ref={pickerRef}
                className={`
                relative self-start
                md:absolute md:top-1/2 md:-translate-y-2/3
                ${isOwn ? 'md:-left-8' : 'md:-right-8'}
            `}
            >
                <div className="flex flex-col gap-1">
                    <button
                        type="button"
                        onClick={onReply}
                        className={`
        h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm
        flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50
        dark:bg-zinc-800 dark:border-zinc-600 dark:text-gray-300 dark:hover:bg-zinc-700
        transition-all

        md:opacity-0 md:pointer-events-none
        md:group-hover/message:opacity-100 md:group-hover/message:pointer-events-auto
    `}
                        title="Reply"
                    >
                        <FiCornerUpLeft className="w-3 h-3" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setShowPicker(prev => !prev)}
                        className={`
                    h-6 w-6 rounded-full bg-white border border-gray-200 shadow-sm
                    flex items-center justify-center text-gray-500 hover:text-blue-600 hover:bg-blue-50
                    dark:bg-zinc-800 dark:border-zinc-600 dark:text-gray-300 dark:hover:bg-zinc-700
                    transition-all

                    md:opacity-0 md:pointer-events-none
                    md:group-hover/message:opacity-100 md:group-hover/message:pointer-events-auto
                    ${showPicker ? 'md:opacity-100 md:pointer-events-auto' : ''}
                `}
                        title="React"
                    >
                        <FiSmile className="w-3 h-3" />
                    </button>
                </div>
                {showPicker && (
                    <div
                        className={`
                        absolute bottom-8 z-30 flex items-center gap-1 rounded-full border border-gray-200 bg-white p-1.5 shadow-xl
                        dark:bg-zinc-800 dark:border-zinc-600
                        ${isOwn ? 'right-0' : 'left-0'}
                    `}
                    >
                        {QUICK_REACTIONS.map(emoji => (
                            <button
                                key={emoji}
                                type="button"
                                onClick={() => handleReactionClick(emoji)}
                                className={`
                                w-8 h-8 rounded-full text-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-zinc-700 transition-transform hover:scale-110
                                ${myReaction?.emoji === emoji ? 'bg-blue-100 dark:bg-blue-900/40' : ''}
                            `}
                            >
                                {emoji}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Actual reactions under message */}
            {Object.values(groupedReactions).length > 0 && (
                <div
                    className={`
                    flex flex-wrap gap-1 mt-1 px-1
                    ${isOwn ? 'justify-end self-end' : 'justify-start self-start'}
                `}
                >
                    {Object.values(groupedReactions).map(group => (
                        <button
                            key={group.emoji}
                            type="button"
                            onClick={() => handleReactionClick(group.emoji)}
                            title={group.users.join(', ')}
                            className={`
                            h-6 px-2 rounded-full text-xs flex items-center gap-1 border shadow-sm transition-all
                            ${group.reactedByMe
                                    ? 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-200'
                                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-zinc-800 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700'
                                }
                        `}
                        >
                            <span>{group.emoji}</span>
                            <span className="font-semibold">{group.count}</span>
                        </button>
                    ))}
                </div>
            )}
        </>
    );
};

export default MessageReactions;